import React from "react";
import styled from "styled-components";
import { theme } from "styles/theme";

type ButtonVariant = "primary" | "secondary" | "danger" | "ghost";

const StyledButton = styled.button<{ $variant: ButtonVariant }>`
  min-height: 44px;
  min-width: 44px;
  padding: 0.625rem 1.25rem;
  border-radius: ${theme.borderRadius.small};
  font-size: ${theme.fontSizes.small};
  font-weight: ${theme.fontWeights.medium};
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease,
    color 0.2s ease;
  border: 1px solid transparent;

  ${({ $variant }) => {
    switch ($variant) {
      case "secondary":
        return `
          background-color: ${theme.colors.tertiary};
          color: ${theme.colors.onTertiary};
          border-color: ${theme.colors.tertiary};
          &:hover { background-color: ${theme.colors.tertiaryDark}; }
        `;
      case "danger":
        return `
          background-color: ${theme.colors.error};
          color: ${theme.colors.white};
          border-color: ${theme.colors.error};
          &:hover { opacity: 0.9; }
        `;
      case "ghost":
        return `
          background-color: transparent;
          color: ${theme.colors.tertiary};
          border-color: ${theme.colors.tertiary};
          &:hover { background-color: ${theme.colors.tertiaryTransparent}; }
        `;
      default:
        return `
          background-color: ${theme.colors.tertiary};
          color: ${theme.colors.onTertiary};
          border-color: ${theme.colors.tertiary};
          &:hover { background-color: ${theme.colors.tertiaryDark}; border-color: ${theme.colors.tertiaryDark}; }
        `;
    }
  }}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid ${theme.colors.tertiary};
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

interface AppButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
}

export function AppButton({
  variant = "primary",
  type = "button",
  children,
  ...rest
}: AppButtonProps) {
  return (
    <StyledButton type={type} $variant={variant} {...rest}>
      {children}
    </StyledButton>
  );
}
